import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface AnalyticsRow {
  readonly knowledgeAnalyticsId: string;
  readonly metricCode: string;
  readonly metricName: string;
  readonly entityType: string | null;
  readonly metricValue: string;
  readonly metricUnit: string | null;
  readonly status: string;
}

interface MetricSummary {
  readonly metricCode: string;
  readonly count: number;
  readonly sum: number;
  readonly avg: number;
  readonly latest: number | null;
}

/**
 * M35 — Knowledge Analytics Dashboard (§33). Register of recorded knowledge
 * metrics (T475) with their value and unit, plus a §24 metric-summary panel
 * that aggregates a chosen metric code into its count, sum, average and latest
 * value. Filterable by metric code and entity type. Tenant isolation is
 * enforced server-side (LAW 4).
 */
export function KnowledgeAnalyticsDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<AnalyticsRow[]>([]);
  const [total, setTotal] = useState(0);
  const [metricCode, setMetricCode] = useState('');
  const [entityType, setEntityType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [summaryCode, setSummaryCode] = useState('');
  const [summary, setSummary] = useState<MetricSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (metricCode) params.set('metricCode', metricCode);
    if (entityType) params.set('entityType', entityType);
    const qs = params.toString();
    apiRequest<{ data: AnalyticsRow[]; total: number }>(`/v1/kms/analytics${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, metricCode, entityType]);

  useEffect(() => load(), [load]);

  const summarise = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setSummaryError(null);
    setSummary(null);
    if (!summaryCode.trim()) {
      setSummaryError(t('m35.analytics.metricRequired'));
      return;
    }
    setBusy(true);
    try {
      const params = new URLSearchParams({ metricCode: summaryCode.trim() });
      const r = await apiRequest<{ data: MetricSummary }>(`/v1/kms/analytics/summary?${params.toString()}`, { token });
      setSummary(r.data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setSummaryError(t('m35.analytics.notAuthorised'));
      } else {
        setSummaryError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="analytics-dashboard">
      <h1>{t('m35.analytics.title')}</h1>

      <form aria-label="summary-form" onSubmit={(e) => void summarise(e)}>
        <label>
          {t('m35.analytics.metricCode')}
          <input aria-label="field-metriccode" value={summaryCode} onChange={(e) => setSummaryCode(e.target.value)} placeholder={t('m35.analytics.metricPh')} />
        </label>
        <Button variant="primary" type="submit" aria-label="submit-summary" disabled={busy}>{busy ? t('m35.analytics.summarising') : t('m35.analytics.summarise')}</Button>
      </form>
      {summaryError && <p role="alert" aria-label="summary-error">{summaryError}</p>}
      {summary && (
        <dl role="status" aria-label="summary-result">
          <dt>{t('m35.analytics.metricCode')}</dt><dd aria-label="summary-code">{summary.metricCode}</dd>
          <dt>{t('m35.analytics.count')}</dt><dd aria-label="summary-count">{summary.count}</dd>
          <dt>{t('m35.analytics.sum')}</dt><dd aria-label="summary-sum">{summary.sum}</dd>
          <dt>{t('m35.analytics.average')}</dt><dd aria-label="summary-avg">{summary.avg}</dd>
          <dt>{t('m35.analytics.latest')}</dt><dd aria-label="summary-latest">{summary.latest ?? '—'}</dd>
        </dl>
      )}

      <div className="filters">
        <label>
          {t('m35.analytics.metricCode')}
          <input aria-label="filter-metriccode" value={metricCode} onChange={(e) => setMetricCode(e.target.value)} placeholder={t('m35.analytics.metricCode')} />
        </label>
        <label>
          {t('m35.analytics.entityType')}
          <input aria-label="filter-entitytype" value={entityType} onChange={(e) => setEntityType(e.target.value)} placeholder={t('m35.analytics.entityType')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m35.common.loading')}</p> : (
        <>
          <p aria-label="analytics-count">{t('m35.analytics.total', { n: total })}</p>
          <DataTable<AnalyticsRow>
            ariaLabel="analytics-table"
            testId="analytics-table"
            rowKey={(r) => r.knowledgeAnalyticsId}
            rowDataAttrs={(r) => ({ 'data-status': r.status })}
            rows={rows}
            columns={[
              { key: 'code', header: t('m35.common.code'), render: (r) => r.metricCode },
              { key: 'name', header: t('m35.common.name'), render: (r) => r.metricName },
              { key: 'entity', header: t('m35.analytics.entity'), render: (r) => r.entityType ?? '—' },
              { key: 'value', header: t('m35.analytics.value'), render: (r) => r.metricValue, cellAriaLabel: (r) => `metric-value-${r.metricCode}` },
              { key: 'unit', header: t('m35.analytics.unit'), render: (r) => r.metricUnit ?? '—' },
              { key: 'status', header: t('m35.common.status'), render: (r) => r.status },
            ]}
          />
        </>
      )}
    </main>
  );
}
